/**
 * Per-request logging scope.
 *
 * Request middleware used to swap module-global context providers, so concurrent
 * requests read each other's requestId/userId. A scope gives each request its own
 * mutable slot. Node servers back it with AsyncLocalStorage via
 * `@aix/apiutils/src/requestScope`; this file stays free of node built-ins because
 * browser bundles import the emit module.
 *
 * Outside any scope (cron jobs, browsers, servers that never install a store),
 * callers fall back to the legacy module-global slot.
 */

export interface RequestScope {
  /** Enclosing scope; unset slots inherit from it. */
  readonly parent: RequestScope | undefined
}

export interface RequestScopeStore {
  getStore(): RequestScope | undefined
  run<T>(scope: RequestScope, fn: () => T): T
}

let scopeStore: RequestScopeStore | null = null

export function installRequestScopeStore(store: RequestScopeStore | null): void {
  scopeStore = store
}

export function getRequestScope(): RequestScope | undefined {
  return scopeStore?.getStore()
}

type RequestIsolation = <T>(fn: () => T) => T

let isolateRequest: RequestIsolation = (fn) => fn()

/** Error reporters (Sentry) register per-request isolation here, keeping this module SDK-agnostic. */
export function setRequestIsolation(isolation: RequestIsolation): void {
  isolateRequest = isolation
}

/** Run `fn` in a fresh scope nested in the current one, plus any registered isolation. */
export function runInRequestScope<T>(fn: () => T): T {
  const run = () => isolateRequest(fn)
  if (!scopeStore) return run()
  return scopeStore.run({ parent: scopeStore.getStore() }, run)
}

/**
 * A value slot that is scoped per request when a scope is active, else module-global.
 * `get` walks enclosing scopes so a nested scope sees its parent's value until it sets its own.
 * Values set outside any scope (e.g. a boot-time default) are never visible inside one.
 */
export function createRequestScopedSlot<T>(): {
  get: () => T | null
  set: (value: T | null) => void
} {
  const scoped = new WeakMap<RequestScope, T | null>()
  let unscoped: T | null = null

  return {
    get: () => {
      for (let scope = getRequestScope(); scope; scope = scope.parent) {
        if (scoped.has(scope)) return scoped.get(scope) ?? null
      }
      return getRequestScope() ? null : unscoped
    },
    set: (value) => {
      const scope = getRequestScope()
      if (scope) scoped.set(scope, value)
      else unscoped = value
    },
  }
}
